import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChevronLeft, Lock, UserRound, TrendingUp, CalendarDays, Activity, ClipboardList,
  Film, Users, Gift, MessageSquare, Award, GitCompare, Download,
} from "lucide-react";
import { Tile, InsightsRibbon } from "@/pages/coach/analytics/shared";
import {
  ProfileTile, GrowthTile, SessionTile, WevaTile, PlansTile, ContentTile,
  CirclesTile, AffiliateTile, EngagementTile, GoalsTile, CompareTile,
} from "@/pages/coach/analytics/tiles";
import { ReportsTile } from "@/pages/coach/analytics/ReportsTile";

const PERIODS = ["week", "month", "quarter", "year", "all"];

export default function Analytics() {
  const { user } = useAuth();
  const t = useT();
  const isCoachPro = String(user?.app_subscription?.tier || "").startsWith("coach_pro");

  const [period, setPeriod] = useState("month");
  const [compareA, setCompareA] = useState("month");
  const [compareB, setCompareB] = useState("year");

  const [profile, setProfile] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [weva, setWeva] = useState(null);
  const [emoDrop, setEmoDrop] = useState(null);
  const [plans, setPlans] = useState(null);
  const [content, setContent] = useState(null);
  const [circles, setCircles] = useState(null);
  const [affiliate, setAffiliate] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [goals, setGoals] = useState(null);
  const [compare, setCompare] = useState(null);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "coach") return;
    const get = (url) => api.get(url).then((r) => r.data).catch(() => null);
    get("/coach/analytics-v2/profile-summary").then(setProfile);
    get("/coach/analytics-v2/client-growth?months=6").then(setGrowth);
    get("/coach/analytics-v2/circles-comparison").then(setCircles);
    get("/coach/analytics-v2/affiliate-performance?period=year").then(setAffiliate);
    get("/coach/analytics-v2/insights").then(setInsights);
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== "coach") return;
    const get = (url) => api.get(url).then((r) => r.data).catch(() => null);
    setSessions(null); setWeva(null); setPlans(null); setContent(null); setEngagement(null); setGoals(null);
    get(`/coach/analytics-v2/session-analysis?period=${period}`).then(setSessions);
    get(`/coach/analytics-v2/weva-group?period=${period}`).then(setWeva);
    get("/coach/analytics/breakout-emotions?days=90").then(setEmoDrop);
    get(`/coach/analytics-v2/weekly-plans?period=${period}`).then(setPlans);
    get(`/coach/analytics-v2/content-performance?period=${period}`).then(setContent);
    get(`/coach/analytics-v2/engagement?period=${period}`).then(setEngagement);
    get(`/coach/analytics-v2/client-goals?period=all`).then(setGoals);
  }, [user, period]);

  useEffect(() => {
    if (!user || user.role !== "coach") return;
    setCompare(null);
    api.get(`/coach/analytics-v2/compare-periods?a=${compareA}&b=${compareB}`).then((r) => setCompare(r.data)).catch(() => setCompare({}));
  }, [user, compareA, compareB]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const body = (data, Comp, props = {}) => (data ? <Comp {...props} /> : <Skeleton className="h-20 rounded-xl" />);

  return (
    <div data-testid="coach-analytics-page">
      <Seo title={t("an.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="analytics-back">
        <ChevronLeft className="h-4 w-4" /> {t("an.back")}
      </Link>
      <PageHeader eyebrow={t("an.eyebrow")} title={t("an.title")} subtitle={t("an.subtitle")} />

      <InsightsRibbon insights={insights} />

      {!isCoachPro && (
        <div className="mt-4 flex items-center gap-3 rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-4" data-testid="an-pro-lock">
          <Lock className="h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">{t("an.pro_locked_title")}</p>
            <p className="text-xs text-muted-foreground">{t("an.pro_locked_sub")}</p>
          </div>
          <Button asChild size="sm" className="shrink-0 rounded-full"><Link to="/app/membership">{t("an.pro_upgrade")}</Link></Button>
        </div>
      )}

      {/* Global period selector */}
      <div className="mt-5 flex flex-wrap items-center gap-1.5" data-testid="an-period-row">
        {PERIODS.map((p) => (
          <button key={p} onClick={() => setPeriod(p)} data-testid={`an-period-${p}`} className={`rounded-full px-3 py-1.5 text-xs font-medium ${period === p ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-accent"}`}>
            {t(`an.period_${p}`)}
          </button>
        ))}
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">{t("an.period_sync")}</p>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <Tile num={1} title={t("an.t1")} icon={UserRound} period={period}>{body(profile, ProfileTile, { profile })}</Tile>
        <Tile num={2} title={t("an.t2")} icon={TrendingUp} period={period}>{body(growth, GrowthTile, { growth })}</Tile>
        <Tile num={3} title={t("an.t3")} icon={CalendarDays} period={period}>{body(sessions, SessionTile, { sessions })}</Tile>
        <Tile num={4} title={t("an.t4")} icon={Activity} period={period}>{body(weva, WevaTile, { weva, emoDrop })}</Tile>
        <Tile num={5} title={t("an.t5")} icon={ClipboardList} period={period}>{body(plans, PlansTile, { plans })}</Tile>
        <Tile num={6} title={t("an.t6")} icon={Film} period={period}>{body(content, ContentTile, { content })}</Tile>
        <Tile num={7} title={t("an.t7")} icon={Users} period={period}>{body(circles, CirclesTile, { circles })}</Tile>
        <Tile num={8} title={t("an.t8")} icon={Gift} period={period}>{body(affiliate, AffiliateTile, { affiliate })}</Tile>
        <Tile num={9} title={t("an.t9")} icon={MessageSquare} period={period}>{body(engagement, EngagementTile, { engagement })}</Tile>
        <Tile num={10} title={t("an.t10")} icon={Award} period={period}>{body(goals, GoalsTile, { goals })}</Tile>
        <Tile num={11} title={t("an.t11")} icon={GitCompare} period={period}>
          <CompareTile compare={compare} periodA={compareA} periodB={compareB} onA={setCompareA} onB={setCompareB} />
        </Tile>
        <Tile num={12} title={t("an.t12")} icon={Download} period={period}><ReportsTile period={period} /></Tile>
      </div>
    </div>
  );
}
